import { useRoleController } from "../components/useRoleController";
import { useGetAllEligibilityByPatientID } from "../queries/eligibility/use-get-all-eligibility-by-patientID";
import { getXtenantId } from "../utils/utils";
import { DisclosureHost } from "./use-disclosure";
import { useReduceDisclosure } from "./use-reduce-disclosure";

interface UseEligibilityVerificationProps {
  /** Patient whose eligibility records should be fetched. */
  patientId?: string;
  /** Primary insurance of the patient; eligibility checks require a valid one. */
  primaryIns?: { uuid?: string } | null;
}

export interface UseEligibilityVerificationReturn {
  /** Whether the current user is allowed to view / check eligibility. */
  hasViewEligibility: boolean;
  /** Most recent eligibility record for the patient (null when none). */
  latestEligibilityRecord: any;
  /** True when an eligibility check can be triggered for the patient. */
  canCheckEligibility: boolean;
  checkEligibilityDisclosure: DisclosureHost;
  eligibilityResponseDisclosure: DisclosureHost;
  eligibilityReportDisclosure: DisclosureHost;
  refetchEligibility: () => Promise<any>;
  /** Opens the "Check Eligibility" / "Recheck" modal for the patient. */
  openCheckEligibility: () => void;
  /** Opens the response modal for the latest (or given) eligibility record. */
  openEligibilityResponse: (record?: any) => void;
  /** Refetches eligibility after a check and opens the response modal. */
  handleEligibilityChecked: () => void;
}

/**
 * Encapsulates the eligibility-verification state and handlers shared by the
 * appointment view: permission check, latest record query, the related
 * disclosures and the open/recheck/checked handlers.
 *
 * @author rajkumar rathod
 */
export const useEligibilityVerification = ({
  patientId,
  primaryIns,
}: UseEligibilityVerificationProps): UseEligibilityVerificationReturn => {
  // ECRH-4589: hide Check Eligibility / Recheck when the user lacks permission.
  const { hasView: hasViewEligibility } =
    useRoleController("Check Eligibility");

  const { data: latestEligibilityData, refetch: refetchEligibility } =
    useGetAllEligibilityByPatientID({
      patientId,
      xTENANTID: getXtenantId(),
      pagination: { page: 0, pageSize: 1 },
    });

  const latestEligibilityRecord =
    latestEligibilityData?.data?.content?.[0] ?? null;

  const [
    eligibilityResponseDisclosure,
    checkEligibilityDisclosure,
    eligibilityReportDisclosure,
  ] = useReduceDisclosure(3);

  const canCheckEligibility = !!primaryIns?.uuid;

  const openCheckEligibility = () => {
    if (canCheckEligibility) {
      checkEligibilityDisclosure.open(patientId);
    }
  };

  const openEligibilityResponse = (record?: any) => {
    eligibilityResponseDisclosure.open(record ?? latestEligibilityRecord);
  };

  const handleEligibilityChecked = () => {
    refetchEligibility().then((res) => {
      const record = res?.data?.data?.content?.[0];
      if (record) {
        eligibilityResponseDisclosure.open(record);
      }
    });
  };

  return {
    hasViewEligibility,
    latestEligibilityRecord,
    canCheckEligibility,
    checkEligibilityDisclosure,
    eligibilityResponseDisclosure,
    eligibilityReportDisclosure,
    refetchEligibility,
    openCheckEligibility,
    openEligibilityResponse,
    handleEligibilityChecked,
  };
};
